import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Mail, Phone, Percent, LogOut, CreditCard, Save, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function TechProfile() {
  const { user, logout } = useAuth();
  const [iban, setIban] = useState("");
  const [phone, setPhone] = useState("");
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setIban(user.iban || "");
      setPhone(user.phone || "");
    }
  }, [user]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me", { iban: iban.trim(), phone: phone.trim() });
      toast.success("Profil güncellendi");
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!newPwd || newPwd.length < 4) { toast.error("Yeni şifre en az 4 karakter"); return; }
    try {
      await api.put("/auth/me", { password: newPwd });
      toast.success("Şifre güncellendi");
      setOldPwd(""); setNewPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="space-y-4" data-testid="tech-profile">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Hesap</div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">Profil</h1>
      </div>

      <Card className="p-5 border border-slate-200 shadow-none">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
          <div className="h-14 w-14 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-display font-bold text-xl">
            {user?.name?.split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="font-display text-lg font-bold text-slate-900">{user?.name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1.5"><Mail className="h-3 w-3" /> {user?.email}</div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5"><Percent className="h-3 w-3" /> %{user?.commission_percent} şirkete borç oranı</div>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Telefon</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0555 555 55 55" data-testid="profile-phone-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> IBAN</Label>
            <Input value={iban} onChange={(e) => setIban(e.target.value)} placeholder="TR00 0000 0000 0000 0000 0000 00" data-testid="profile-iban-input" className="font-mono" />
            <div className="text-xs text-slate-500 mt-1">Şirketin tahsilat ve prim ödemeleri için kullanacağı banka hesabı.</div>
          </div>
          <Button onClick={saveProfile} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="profile-save-button">
            <Save className="h-4 w-4 mr-2" /> Profili Kaydet
          </Button>
        </div>
      </Card>

      <Card className="p-5 border border-slate-200 shadow-none">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="h-4 w-4 text-slate-500" />
          <h3 className="font-semibold text-slate-900">Şifre Değiştir</h3>
        </div>
        <div className="space-y-3">
          <div>
            <Label>Yeni Şifre</Label>
            <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="••••••••" data-testid="profile-newpwd-input" />
          </div>
          <Button variant="outline" onClick={changePassword} disabled={!newPwd} className="w-full">
            Şifreyi Güncelle
          </Button>
        </div>
      </Card>

      <Button variant="outline" className="w-full text-rose-600" onClick={logout} data-testid="profile-logout">
        <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
      </Button>
    </div>
  );
}
